<template>
  <v-dialog
    :model-value="confirmTagPrintDialog"
    content-class="dialog_width_print"
    :lock-scroll="false"
    scrollable
    @update:model-value="(val) => $emit('update:confirmTagPrintDialog', val)"
  >
    <!-- 印刷対象部分をラップ -->
    <div ref="printableContent" class="printable-content">
      <!-- 印刷対象内容 -->
      <!-- 8件ごとにグループ化 -->
      <div
        v-for="(row, rowIndex) in printShippingData"
        :key="rowIndex"
        class="print-group"
      >
        <v-card flat border>
          <table class="print_shipping_main_table">
            <tbody>
              <tr v-for="n in 4" :key="n">
                <td v-for="m in 2" :key="m" class="print_shipping_main_td">
                  <p v-if="n != 1" class="p1 line_break">&nbsp;</p>
                  <table class="print_shipping_sub_table">
                    <tbody>
                      <tr>
                        <td align="left" class="print_shipping_sub_td_m">
                          <span class="print_shipping_span_tiltle">
                            受付番号(下5桁) </span
                          >&nbsp;
                        </td>
                      </tr>
                      <tr>
                        <td align="right" class="print_shipping_sub_td_l">
                          <span class="print_shipping_span_data"
                            >{{ row ? row.receptionNo.slice(-5) : "" }} </span
                          >&nbsp;
                        </td>
                      </tr>
                      <tr>
                        <td align="left" class="print_shipping_sub_td_m">
                          <span class="print_shipping_span_tiltle">
                            個口数(合計) </span
                          >&nbsp;
                        </td>
                      </tr>
                      <tr>
                        <td align="right" class="print_shipping_sub_td_l">
                          <span class="print_shipping_span_data">
                            {{ row ? row.unit : "" }}
                            個 </span
                          >&nbsp;
                        </td>
                      </tr>
                      <tr>
                        <td align="left" class="print_shipping_sub_td_m">
                          <span class="print_shipping_span_tiltle">
                            お客様名 (かな) </span
                          >&nbsp;
                        </td>
                      </tr>
                      <tr>
                        <td align="right" class="print_shipping_sub_td_m">
                          <span class="print_shipping_span_name">
                            {{ row ? row.nameKana : "" }}
                            様
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="print_shipping_sub_td_spacer">&nbsp;</td>
                      </tr>
                      <tr>
                        <td align="right" class="print_shipping_sub_td_shop">
                          <span class="print_shipping_span_shop">
                            {{ row ? formatPrintDate(row.receptionDate) : "" }}
                            {{ row ? row.areaName : "" }}-{{
                              row ? row.shopName : ""
                            }} </span
                          >&nbsp;
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <p v-if="n != 4" class="p2 line_break">&nbsp;</p>
                </td>
              </tr>
            </tbody>
          </table>
        </v-card>
      </div>
    </div>
    <v-overlay
      :model-value="overlay"
      class="d-flex align-center justify-center"
    >
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script setup lang="ts">
import { PropType, ref } from 'vue';

// テンプレート内のref="printableContent"への参照
const printableContent = ref<HTMLElement | null>(null);

interface PrintTagDataItem {
  receptionNo: string;
  unit: number;
  nameKana: string;
  receptionDate: string;
  areaName: string;
  shopName: string;
}

defineProps({
  confirmTagPrintDialog: {
    type: Boolean,
    required: true,
  },
  printShippingData: {
    type: Array as PropType<PrintTagDataItem[]>,
    required: false,
    default: () => [],
  },
  overlay: {
    type: Boolean,
    required: true,
  },
});

defineEmits<{
  (e: 'update:confirmTagPrintDialog', value: boolean): void;
}>();

const showPrintTab = (content: string) => {
  // 印刷用のCSSを文字列として定義
  const printCSS = `
    <style>
      /* 印刷ページの余白を調整 */
      @page {
        margin: 5mm;
      }

      .print_shipping_main_table {
        display: table !important;
        justify-content: space-between;
        align-items: center;
        padding: 5px 0;
        border-bottom: 1px solid var(--boder-color);
        font-family: "Hiragino Kaku Gothic Pro", "ヒラギノ角ゴ Pro W3", Osaka,
          "ＭＳ Ｐゴシック", sans-serif, Tahoma, "Lucida Grande", Verdana, sans-serif,
          Verdana, Helvetica, Arial;
        line-height: 1.5;
        width: 800px;
        border-collapse: collapse;
        page-break-inside: avoid; /* テーブル内部での改ページを避ける */
        font-size: 14px;
      }
      .print_shipping_main_td {
        width: 530px;
      }

      .print_shipping_main_table tbody {
        display: table-row-group !important;
      }
      .print_shipping_main_table tr {
        display: table-row !important;
      }
      .print_shipping_main_table td {
        display: table-cell !!important;
      }

      .print_shipping_sub_table {
        width: 100%;
        font-size: 12px;
      }
      .print_shipping_sub_td_m {
        height: 26px;
        line-height: 16px;
      }

      .print_shipping_sub_td_l {
        height: 36px;
      }

      .print_shipping_sub_td_spacer {
        height: 14px;
      }
      .print_shipping_sub_td_shop {
        height: 22px;
      }

      .print_shipping_span_tiltle {
        font-size: 16px;
        padding-left: 36px;
      }

      .print_shipping_span_data {
        font-size: 24px;
        font-weight: bold;
        padding-right: 30px;
        line-height: 16px;
      }

      .print_shipping_span_name {
        font-size: 24px;
        padding-right: 32px;
      }

      .print_shipping_span_shop {
        font-size: 14px;
        padding-right: 32px;
      }

      /* 印刷ボタン */
      .print_button {
        display: block;
        text-align: center;
        vertical-align: middle;
        text-decoration: none;
        width: 168px;
        border: 2px solid #2196f3;
        background: #2196f3;
        color: #fff;
        border-radius: 5px;
        transition: 0.5s;
        animation: move_a 2s infinite;
        cursor: pointer;
        height: 36px;
      }

      .print_button:hover {
        opacity: 0.9;
      }

      .line_break {
        margin:0px !important;
        margin-top:5px !important;
        margin-bottom:7px !important;
      }

      /* 印刷時のスタイル */
      @media print {
        /* 印刷 */
        #printableButton {
          display: none;
        }

        /* 8件ごとに改ページ */
        .print-group {
          display: block;
          page-break-inside: avoid;
          page-break-after: always;
        }
        .print-group:last-child {
          page-break-after: auto;
        }
      }
    </style>
  `;

  // 新規タブを開く
  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>印刷プレビュー</title>
          ${printCSS}
        </head>
        <body>
          <div id="printableButton">
            <button type="button" class="print_button" onclick="window.print();">
                <span>印刷する</span>
            </button>
          </div>
          ${content}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
  }
};

const formatPrintDate = (dateString: string) => {
  const date = new Date(dateString);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const day = ("0" + date.getDate()).slice(-2);
  const hours = ("0" + date.getHours()).slice(-2);
  const minutes = ("0" + date.getMinutes()).slice(-2);
  return `${month}/${day} ${hours}:${minutes}`;
};

defineExpose({
  showPrintTab,
  formatPrintDate,
  printableContent,
});
</script>

<style scoped>
/* スタイルをここに追加 */
</style>
